import React, { forwardRef, useId, useState, useCallback, useMemo } from 'react';
import { InputText, InputTextProps } from 'primereact/inputtext';
import { useTranslation } from 'localization';
import './LocalizedInputAtom.scss';

/** Supported locales for translations */
export const DEFAULT_LOCALES = ['en-US', 'fr-FR'] as const;

export interface LocalizedValue {
  default: string;
  translations?: Record<string, string>;
}

export interface LocalizedInputAtomProps
  extends Omit<InputTextProps, 'value' | 'onChange'> {
  /** The localized value object */
  value: LocalizedValue;
  /** Callback when value changes */
  onChange: (value: LocalizedValue) => void;
  /** Available locales for translation (defaults to en-US, fr-FR) */
  locales?: readonly string[];
  /** Label displayed above the input */
  label?: string;
  /** Placeholder for the default input */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
}

const LocalizedInputAtom = forwardRef<HTMLDivElement, LocalizedInputAtomProps>(
  (
    {
      value,
      onChange,
      locales = DEFAULT_LOCALES,
      label,
      placeholder,
      disabled = false,
      className = '',
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    // The visible `label` binds to the default-text input by id, minted when the caller
    // gives none — an unbound <label> named nothing.
    const mintedId = useId();
    const inputId = rest.id ?? mintedId;

    const hasTranslations = useMemo(() => {
      if (!value.translations) return false;
      return Object.values(value.translations).some((t) => t.length > 0);
    }, [value.translations]);

    const handleDefaultChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ...value, default: e.target.value });
      },
      [value, onChange]
    );

    const handleTranslationChange = useCallback(
      (locale: string, text: string) => {
        const translations = { ...value.translations };
        if (text) {
          translations[locale] = text;
        } else {
          delete translations[locale];
        }
        const hasAny = Object.keys(translations).length > 0;
        onChange({
          ...value,
          translations: hasAny ? translations : undefined,
        });
      },
      [value, onChange]
    );

    const toggleExpanded = useCallback(() => {
      setExpanded((prev) => !prev);
    }, []);

    return (
      <div
        ref={ref}
        className={`localized-input-atom ${expanded ? 'localized-input-atom--expanded' : ''} ${className}`.trim()}
      >
        {label && (
          <label className="localized-input-atom__label" htmlFor={inputId}>{label}</label>
        )}
        <div className="localized-input-atom__default-row">
          <InputText
            value={value.default}
            onChange={handleDefaultChange}
            placeholder={placeholder}
            disabled={disabled}
            className="localized-input-atom__input"
            {...rest}
            id={inputId}
          />
          <button
            type="button"
            className={`localized-input-atom__globe-btn ${hasTranslations ? 'localized-input-atom__globe-btn--active' : ''}`}
            onClick={toggleExpanded}
            disabled={disabled}
            title={t('localizedInput.manageTranslations')}
            aria-label={t('localizedInput.manageTranslations')}
            aria-expanded={expanded}
          >
            <i className="pi pi-globe" />
          </button>
        </div>
        {expanded && (
          <div className="localized-input-atom__translations">
            {locales.map((locale) => (
              <div
                key={locale}
                className="localized-input-atom__translation-row"
              >
                <span className="localized-input-atom__locale-tag">
                  {locale}
                </span>
                <InputText
                  value={value.translations?.[locale] || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleTranslationChange(locale, e.target.value)
                  }
                  placeholder={t('localizedInput.translationPlaceholder', { locale })}
                  aria-label={t('localizedInput.translationPlaceholder', { locale })}
                  disabled={disabled}
                  className="localized-input-atom__input"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

LocalizedInputAtom.displayName = 'LocalizedInputAtom';
export default LocalizedInputAtom;
